import * as React from 'react';
import styled from '@emotion/styled';
import { ChartLegend, DropdownMenu, OverlappedBarsChart } from '@liveoptics/react-ui';
import { Series } from '@liveoptics/react-ui/dist/types/interfaces/Charts';
import {
  ProvisioningContrastGraphOption,
  ProvisioningContrastPoint
} from '../models/views/provisioningContrastGraphViewModel';
import { format } from 'd3-format';

const ChartContainer = styled.div`
  position: absolute;
  display: contents;
`;

const ChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-top: 20px;
`;

const DescriptionParagraph = styled.p`
  max-width: 900px;
  font-size: 14px;
  line-height: 17px;
  letter-spacing: 0.28px;
  color: #566072;
  opacity: 1;
  user-select: none;
  flex: 1;
  text-align: justify;
  margin-bottom: 0px;
  margin-right: 4rem;
`;

const TooltipP = styled.p`
  font-family: 'Open Sans', sans-serif;
  color: #566072;
  font-weight: 500;
overflow-y: inherit;
`;

const DropdownMenuContainer = styled.div`
  padding: 25px 0px 0px 370px;
`;

interface Props {
  data: ProvisioningContrastPoint[] | undefined;
  showGraph: boolean;
}

const LegendWrapper = styled.div`
  align-self: flex-end;
  margin-bottom: 25px;
  min-width: max-content;
`;

interface State {
  selectedGraph: ProvisioningContrastGraphOption;
}

const legendItems = [
  { color: '#60D1BF', label: 'Number of vCPUs' },
  { color: '#00A4CA', label: 'Memory (GiB)' },
  { color: '#F2AF00', label: 'Capacity (GiB)' }
];

const GetGraphTypeFromName = (graphName: string) => {
  switch (graphName) {
    case 'vCPU over Memory':
      return ProvisioningContrastGraphOption.VCpuOverMempry;
    case 'vCPU over Capacity':
      return ProvisioningContrastGraphOption.VCpuOverCapacity;
    case 'Memory over vCPU':
      return ProvisioningContrastGraphOption.MemoryOverVCpu;
    case 'Memory over Capacity':
      return ProvisioningContrastGraphOption.MemoryOverCapacity;
    case 'Capacity over vCPU':
      return ProvisioningContrastGraphOption.CapacityOverVCpu;
    case 'Capacity over Memory':
      return ProvisioningContrastGraphOption.CapacityOverMemory;
    default:
      return ProvisioningContrastGraphOption.VCpuOverMempry;
  }
};

const updateGraphValues = (
  vms: ProvisioningContrastPoint[],
  graphType: ProvisioningContrastGraphOption
): Series<ProvisioningContrastPoint>[][] => {
  let chartSeries: Series<ProvisioningContrastPoint>[][] = [];
  let chartSubSeries: Series<ProvisioningContrastPoint>[] = [];
  let subSeries1: Series<ProvisioningContrastPoint> = { points: [], key: 'series1' };
  let subSeries2: Series<ProvisioningContrastPoint> = { points: [], key: 'series2' };

  switch (graphType) {
    case ProvisioningContrastGraphOption.VCpuOverMempry:
      vms = vms.sort((a, b) => b.VCpu - a.VCpu || b.MemoryGB - a.MemoryGB);
      vms.forEach(vm => {
        subSeries1.points.push({
          Name: vm.Name,
          VCpu: vm.VCpu,
          CapacityGB: vm.CapacityGB,
          MemoryGB: vm.MemoryGB,
          value: vm.VCpu
        });
        subSeries2.points.push({
          Name: vm.Name,
          VCpu: vm.VCpu,
          CapacityGB: vm.CapacityGB,
          MemoryGB: vm.MemoryGB,
          value: vm.MemoryGB * -1
        });
      });
      subSeries1.color = legendItems[0].color;
      subSeries2.color = legendItems[1].color;
      break;
    case ProvisioningContrastGraphOption.VCpuOverCapacity:
      vms = vms.sort((a, b) => b.VCpu - a.VCpu || b.CapacityGB - a.CapacityGB);
      vms.forEach(vm => {
        subSeries1.points.push({
          Name: vm.Name,
          VCpu: vm.VCpu,
          CapacityGB: vm.CapacityGB,
          MemoryGB: vm.MemoryGB,
          value: vm.VCpu
        });
        subSeries2.points.push({
          Name: vm.Name,
          VCpu: vm.VCpu,
          CapacityGB: vm.CapacityGB,
          MemoryGB: vm.MemoryGB,
          value: vm.CapacityGB * -1
        });
      });
      subSeries1.color = legendItems[0].color;
      subSeries2.color = legendItems[2].color;

      break;
    case ProvisioningContrastGraphOption.MemoryOverVCpu:
      vms = vms.sort((a, b) => b.MemoryGB - a.MemoryGB || b.VCpu - a.VCpu);
      vms.forEach(vm => {
        subSeries1.points.push({
          Name: vm.Name,
          VCpu: vm.VCpu,
          CapacityGB: vm.CapacityGB,
          MemoryGB: vm.MemoryGB,
          value: vm.MemoryGB
        });
        subSeries2.points.push({
          Name: vm.Name,
          VCpu: vm.VCpu,
          CapacityGB: vm.CapacityGB,
          MemoryGB: vm.MemoryGB,
          value: vm.VCpu * -1
        });
      });
      subSeries1.color = legendItems[1].color;
      subSeries2.color = legendItems[0].color;
      break;
    case ProvisioningContrastGraphOption.MemoryOverCapacity:
      vms = vms.sort((a, b) => b.MemoryGB - a.MemoryGB || b.CapacityGB - a.CapacityGB);
      vms.forEach(vm => {
        subSeries1.points.push({
          Name: vm.Name,
          VCpu: vm.VCpu,
          CapacityGB: vm.CapacityGB,
          MemoryGB: vm.MemoryGB,
          value: vm.MemoryGB
        });
        subSeries2.points.push({
          Name: vm.Name,
          VCpu: vm.VCpu,
          CapacityGB: vm.CapacityGB,
          MemoryGB: vm.MemoryGB,
          value: vm.CapacityGB * -1
        });
      });
      subSeries1.color = legendItems[1].color;
      subSeries2.color = legendItems[2].color;
      break;
    case ProvisioningContrastGraphOption.CapacityOverVCpu:
      vms = vms.sort((a, b) => b.CapacityGB - a.CapacityGB || b.VCpu - a.VCpu);
      vms.forEach(vm => {
        subSeries1.points.push({
          Name: vm.Name,
          VCpu: vm.VCpu,
          CapacityGB: vm.CapacityGB,
          MemoryGB: vm.MemoryGB,
          value: vm.CapacityGB
        });
        subSeries2.points.push({
          Name: vm.Name,
          VCpu: vm.VCpu,
          CapacityGB: vm.CapacityGB,
          MemoryGB: vm.MemoryGB,
          value: vm.VCpu * -1
        });
      });
      subSeries1.color = legendItems[2].color;
      subSeries2.color = legendItems[0].color;
      break;
    case ProvisioningContrastGraphOption.CapacityOverMemory:
      vms = vms.sort((a, b) => b.CapacityGB - a.CapacityGB || b.MemoryGB - a.MemoryGB);
      vms.forEach(vm => {
        subSeries1.points.push({
          Name: vm.Name,
          VCpu: vm.VCpu,
          CapacityGB: vm.CapacityGB,
          MemoryGB: vm.MemoryGB,
          value: vm.CapacityGB
        });
        subSeries2.points.push({
          Name: vm.Name,
          VCpu: vm.VCpu,
          CapacityGB: vm.CapacityGB,
          MemoryGB: vm.MemoryGB,
          value: vm.MemoryGB * -1
        });
      });
      subSeries1.color = legendItems[2].color;
      subSeries2.color = legendItems[1].color;
      break;
    default:
      vms = vms.sort((a, b) => b.VCpu - a.VCpu || b.MemoryGB - a.MemoryGB);
      vms.forEach(vm => {
        subSeries1.points.push({
          Name: vm.Name,
          VCpu: vm.VCpu,
          CapacityGB: vm.CapacityGB,
          MemoryGB: vm.MemoryGB,
          value: vm.VCpu
        });
        subSeries2.points.push({
          Name: vm.Name,
          VCpu: vm.VCpu,
          CapacityGB: vm.CapacityGB,
          MemoryGB: vm.MemoryGB,
          value: vm.MemoryGB * -1
        });
      });
      subSeries1.color = legendItems[0].color;
      subSeries2.color = legendItems[1].color;
      break;
  }
  chartSubSeries.push(subSeries1);
  chartSubSeries.push(subSeries2);
  chartSeries.push(chartSubSeries);

  return chartSeries;
};

class ProvisioningContrastGraph extends React.Component<Props> {
  constructor(props: Props) {
    super(props);
  }

  state = {
    selectedGraph: ProvisioningContrastGraphOption.VCpuOverMempry
  } as State;

  render() {
    if (this.props.showGraph && this.props.data) {
      const currentSeries = updateGraphValues(this.props.data, this.state.selectedGraph);
      return (
        <ChartContainer>
          {currentSeries.length !== 0 && (
            <>
              <DropdownMenuContainer>
                <DropdownMenu
                  onChange={(ev: { target: { value: string } }) => {
                    this.setState({
                      selectedGraph: GetGraphTypeFromName(ev.target.value)
                    });
                  }}
                >
                  <option value="vCPU over Memory">vCPU over Memory</option>
                  <option value="vCPU over Capacity">vCPU over Capacity</option>
                  <option value="Memory over vCPU">Memory over vCPU</option>
                  <option value="Memory over Capacity">Memory over Capacity</option>
                  <option value="Capacity over vCPU">Capacity over vCPU</option>
                  <option value="Capacity over Memory">Capacity over Memory</option>
                </DropdownMenu>
              </DropdownMenuContainer>
              <ChartHeader>
                <DescriptionParagraph>
                  This graph displays two provisioning properties of each VM. The VMs are sorted
                  highest to lowest by the first property. To learn more about this chart see our{' '}
                  <a href="https://www.dell.com/support/kbdoc/000233565">blog</a>.
                </DescriptionParagraph>
                <LegendWrapper>
                  <ChartLegend legendItems={legendItems} />
                </LegendWrapper>
              </ChartHeader>
              <OverlappedBarsChart
                height={320}
                matrix={currentSeries}
                axisFormatter={val => format('')(Math.abs(val))}
                tooltipFn={point => (
                  <TooltipP>
                    VM Name: {(point as ProvisioningContrastPoint).Name} <br />
                    #vCPU: {(point as ProvisioningContrastPoint).VCpu} <br />
                    Memory (GiB): {(point as ProvisioningContrastPoint).MemoryGB} <br />
                    Capacity (GiB): {(point as ProvisioningContrastPoint).CapacityGB} <br />
                  </TooltipP>
                )}
              ></OverlappedBarsChart>
            </>
          )}
        </ChartContainer>
      );
    } else return <div></div>;
  }
}

export default ProvisioningContrastGraph;
